import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {find, get, has, isEmpty} from 'lodash';
import {IDiagnosticOrder, IRevision} from '@ispm/test/interfaces/diagnostic-order.interface';
import {IWorkflow} from '@ispm/core/interfaces/workflow.interface';
import {ReviseService} from '@ispm/workflow/services/revise.service';
import {IReviseConfig} from '../../../../configuration/interfaces/definition.interface';
import {testViewWithPatientProfile} from '@ispm/test/routes/test-view-state-names';
import {tooltipPositions} from '@ispm/shared/directives/tooltips/tooltip.directive';
import {TestRunningMode} from '@ispm/test/interfaces/test-running-mode.enum';
import {select, Store} from '@ngrx/store';
import {selectUiConfiguration} from '../../../../configuration/store/configuration.selector';
import {takeUntil} from 'rxjs/operators';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {Observable, Subject} from 'rxjs';
import {TestBarEnableConfirmButtonAction} from '@ispm/test/store/test-bar.actions';
import {CONFIRM_BUTTON} from '@ispm/test/components/test-bar/test-bar.component';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {TestConfiguration} from "@ispm/test/interfaces/test-configuration.interface";
import {IWorkflowJobItem} from "@ispm/workflow/interfaces/job-item.interface";
import {PatientInfoService} from "@ispm/user/services/patientInfo.service";
import {PollJobService} from "@ispm/test/services/poll-job.service";
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { ReassignTestModalComponent } from '@ispm/layout/components/modals/reassign-test-modal/reassign-test-modal.component';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { ReassignModalService } from '@ispm/layout/services/reassign-modal.service';
import { TestService } from '../../services/test.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotesService } from '@ispm/user/services/notes.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { AttachmentService } from '@ispm/migration/services/attachment.service';
import { RouterManagerService } from "@ispm/core/services/router-manager.service";
import { Router } from '@angular/router';


export interface TestAction {
  label: string;
  labelAvailable: boolean;
  action: string;
  icon: string;
  actionIconAvailable: boolean;
  enable: boolean;
  permissionId: string;
  primary: boolean;
  actionList?: Array<TestAction>;
  payload?: any;
  redirectUrl?: any;
}

@Component({
  selector: 'ispm-pathway-actions',
  templateUrl: './pathway-actions.component.html',
  styleUrls: ['./pathway-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PathwayActionsComponent implements OnInit, OnDestroy {

  @Input() config: {
    currentTest: IDiagnosticOrder;
    newNotes: number;
    currentWorkflow: IWorkflow;
    currentRevision: IRevision;
    latestRevisionId: string;
    testInWorklist: boolean;
    contextName: string;
  };

  testActionsPresent = false;
  testActions: TestAction[] = [];
  readOnlyParameters: any;
  revisionId: string;
  newNotes: number;
  currentTest: IDiagnosticOrder;
  currentWorkflow: IWorkflow;
  currentRevision: IRevision;
  latestRevisionId: string;
  id: string;
  toolTipPositions = tooltipPositions;
  confirmButtonValue = '';
  testInWorklist: boolean;

  public isPerformer = false;
  public reviseTemplates: Array<{ usage: 'revision', reference: string }> = [];
  public testInDraftMode = false;
  public hideBackActionButton = false;
  public testIsCompleted = false;
  public testConfiguration: TestConfiguration;
  public stopActionItem: TestAction;

  private hideActionButton = false;
  private stepExecutionType: string;
  private revise: IReviseConfig;
  private workflowActionOverride$: Observable<any>;
  private destroyer$: Subject<void> = new Subject();
  private contextName: string;

  constructor(private notesService: NotesService,
              private attachmentsService: AttachmentService,
              private authService: AuthenticationService,
              private workflowService: WorkflowService,
              private notificationMessageService: NotificationMessageService,
              private testService: TestService,
              private coreActionCreator: CoreActionCreatorService,
              private store: Store<IgenomicsState>,
              private changeDetector: ChangeDetectorRef,
              private patientInfoService: PatientInfoService,
              private poolJobSevice: PollJobService,
              private modalLoaderService: DynamicModalLoaderService,
              private userActionCreatorService: UserActionCreatorService,
              private reassignModalService: ReassignModalService,
              private router: Router,
              private routerManagerService: RouterManagerService) {}

  ngOnInit() {
    this.testConfiguration = this.testService.testConfiguration;
    this.contextName = this.config.contextName;

    this.workflowActionOverride$ = this.store.pipe(
      select(selectUiConfiguration(null, 'Test', 'workflowAction')),
      takeUntil(this.destroyer$.asObservable())
    );
    this.workflowActionOverride$.subscribe(workflowAction => {
      this.hideActionButton = get(workflowAction, 'hideActionButton', false);
      this.hideBackActionButton = get(workflowAction, 'hideBackActionButton', false);

      if (!this.hideActionButton) {
        this.updateConfirmButtonBehaviour(get(workflowAction, 'actionButton', undefined));
      }
      this.changeDetector.markForCheck();
    });

    this.testInWorklist = this.config.testInWorklist;

    this.revisionId = this.routerManagerService.parameters?.revisionId;
    this.latestRevisionId = this.config.latestRevisionId;

    this.currentTest = this.config.currentTest;
    this.testInDraftMode = this.currentTest.runningMode === TestRunningMode.DRAFT_MODE;
    this.testIsCompleted = this.currentTest.workflowRequest.status.toLowerCase() !== 'done';

    this.currentWorkflow = this.config.currentWorkflow;
    this.checkIfPerformer(); // uses currentWorkflow
    this.id = this.testConfiguration.id;
    this.revise = this.testConfiguration.revise;
    this.currentRevision = this.config.currentRevision;
    this.validReviseStep(); // uses currentWorkflow and currentRevision

    this.readActions(this.testConfiguration.testActions || [], this.testActions);
    this.testActionsPresent = this.testActions.length > 0;
    this.buildInfo(); // uses testActions

    this.readOnlyParameters = this.reassignModalService.setReadOnlyModeParameters(
      this.testConfiguration.readOnlyMode, this.config.currentTest, this.authService.user);

    this.newNotes = this.config.newNotes;
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private updateConfirmButtonBehaviour(confirmButtonDescriptor: any): void {
    if (confirmButtonDescriptor && confirmButtonDescriptor.type === CONFIRM_BUTTON && this.currentRevision) {

      this.confirmButtonValue = get(confirmButtonDescriptor, 'value', '');
      this.stepExecutionType = get(confirmButtonDescriptor, 'args', undefined);

      this.currentRevision.items.forEach(val => {
        if (val.id === this.currentWorkflow.activeJob) {
          val.inputs.forEach(input => {
            if (input.type === CONFIRM_BUTTON) {
              Object.assign(input, confirmButtonDescriptor);
              if (input.enabledByDefault) {
                this.store.dispatch(new TestBarEnableConfirmButtonAction(true));
              }
            }
          });
        }
      });
    }
  }

  private checkIfPerformer() {
    for (const ref of this.currentWorkflow.references) {
      if (ref.key && ref.key.toLowerCase() === 'practitioner') {
        try {
          const val = JSON.parse(ref.value);
          this.isPerformer = val && val.practType === 'performer' && val.practId === this.authService.user.info.id;
          if (this.isPerformer) {
            break;
          }
        } catch (e) {
          continue;
        }
      }
    }
  }

  private readActions(actions: Array<any>, outArray: Array<TestAction>, parentAction?) {
    if (!actions || actions.length === 0) {
      return;
    }

    for (const action of actions) {
      const actionIconPresent: boolean = !!action.icon;
      const labelPresent: boolean = !!action.label;

      let enable = true;
      if (has(action, 'enable')) {
        enable = get(action, 'enable');
      }

      let subActionsArray: Array<any> = null;
      if (action.type && action.type.toLowerCase() === 'dropdownmenu') {

        action.isDropDownMenu = true;
        subActionsArray = [];
        this.readActions(action.actionList, subActionsArray, action);
      }

      if (parentAction && parentAction.isDropDownMenu === true) {
        if (typeof (action.action) === 'string') {
          const menuActionFunc = this.factorDropDownMenuAction(action.action);
          if (menuActionFunc) {
            action.action = menuActionFunc.bind(this);
          }
        }
      }

      const actionObj: TestAction = {
        label: action.label,
        labelAvailable: labelPresent,
        action: action.action,
        icon: action.icon,
        actionIconAvailable: actionIconPresent,
        enable: enable,
        permissionId: action.permissionId,
        primary: action.primary,
        actionList: subActionsArray,
        payload: action.payload,
        redirectUrl: action.redirectUrl
      };
      outArray.push(actionObj);
    }
  }

  public factorDropDownMenuAction(actionKey: string): any {
    actionKey = actionKey.toLowerCase();
    if (actionKey === 'showpatientanalytics') {
      return this.showPatientAnalytics;
    }
  }

  buildInfo() {
    if (this.testActionsPresent) {
      this.stopActionItem = find(this.testActions, (actionObj) => {
        return actionObj.action.toLowerCase() === 'stop';
      });
    } else {
      const stopAction = get(this.testConfiguration, 'stopAction');
      if (!isEmpty(stopAction)) {
        this.stopActionItem = stopAction;
      }
    }

    if (isEmpty(this.stopActionItem)) {
      this.stopActionItem = {
        label: 'Stop Workflow', labelAvailable: true, action: 'stop',
        icon: '', actionIconAvailable: false, enable: true,
        permissionId: '', primary: false, redirectUrl: ''
      };
      if (this.testActionsPresent) {
        this.testActions.push(this.stopActionItem);
      }
    }
  }

  public hideRemoveFromWorklist(): boolean {
    return get(this.testConfiguration, 'hideRemoveFromWorklist', false);
  }

  public showPatientAnalytics(): void {
    this.router.navigate(['PatientAnalytics'], { queryParams: {
        patientId: this.routerManagerService.parameters?.patientId
      }
    });
  }

  toggleNotes(): void {
    this.notesService.toggleNotes(this.editModeIsSupported());
  }

  toggleAttachments(): void {
    this.attachmentsService.toggleAttachments(this.editModeIsSupported());
  }

  collapseNotes(): void {
    this.notesService.collapseNotes();
  }

  collapseAttachments(): void {
    this.attachmentsService.collapseAttachments();
  }

  setReviseTemplates() {
    this.reviseTemplates = this.testConfiguration.workflowJobsTemplates.filter(
      (item): item is { usage: 'revision'; reference: string; name: string } => item.usage === 'revision');
  }

  validReviseStep() {
    if (this.isPerformer && ReviseService.isReviseDefinitionAvailable(this.testConfiguration, this.currentWorkflow, this.currentRevision)) {
      this.setReviseTemplates();
    }
  }

  getActiveJobItem(): IWorkflowJobItem {
    if (!this.currentRevision) {
      return null;
    }
    return this.currentRevision.items.find(val => val.id === this.currentWorkflow.activeJob);
  }

  goToRevision(workflowID, revisionID) {
    const navigationParams = {
      patientId: this.routerManagerService.parameters?.patientId,
      testId: workflowID,
      revisionId: revisionID
    };
    const navigateTo = `${testViewWithPatientProfile}/${navigationParams.patientId}/${navigationParams.testId}/${navigationParams.revisionId}`;
    const navigationOptions = {reload: true};
    const queryParams = {EMRIntegration: this.routerManagerService.queryParameters?.EMRIntegration};
    this.poolJobSevice.pollingUntilStepAndNavigate(revisionID, workflowID, this.testConfiguration, navigateTo, queryParams, navigationOptions);
  }

  removeFromWorklist(): void {
    let modalPayload: any = {};
		modalPayload.modalName = 'ReamoveModal';
    modalPayload.title = 'Remove test from worklist';

		this.modalLoaderService.popupModal(ReassignTestModalComponent,
		{
			content: modalPayload,
      backdropAllowed: false
		}, ModalTypes.NONE).subscribe((data) => {
			if (data && data.payload && data.payload.action === 'ok') {
				this.coreActionCreator.setLoader(true);
        this.workflowService.getWorklfowById(this.routerManagerService.parameters?.testId)
          .then((workflowResult) => {
            return this.workflowService.removeAnalysisFromWorklist(workflowResult.body,
              this.routerManagerService.parameters?.revisionId,
              this.authService.user.info.id).subscribe((ans) => {
              this.notificationMessageService.success('Test removed from your worklist successfully!');
              this.router.navigate([`Pathologist`]);
            });
          })
          .catch(err => {
            this.notificationMessageService.error('error while removing test from worklist');
          })
          .finally(() => {
            this.coreActionCreator.setLoader(false);
          });
			}
		});
  }

  public reassignIsEnabled(): boolean {
    return this.reassignModalService.reassignIsEnabled(this.readOnlyParameters);
  }

  public reassign(): void {
    const practitionerFilterBy = get(this.readOnlyParameters, 'practitionerFilterBy', undefined);

    const tempAnalysis = {
      activeRevision: this.routerManagerService.parameters?.revisionId,
      workflowrequest_id: this.routerManagerService.parameters?.testId,
      practitionerFilterBy: practitionerFilterBy
    };
    let allowedRolesForReassignment: any;
    this.userActionCreatorService.getStoredOrganizationConfiguration()
    .take(1)
    .map((organization) => {
    allowedRolesForReassignment = organization.allowedRolesForReassignment ? organization.allowedRolesForReassignment : ['Pathologist'];
    }).toPromise();

    let modalPayload: any = {};
    modalPayload.name = this.contextName;
    modalPayload.testNumber = this.currentTest.identifiers[0].value;
    modalPayload.analysis = tempAnalysis;
    modalPayload.practitionerRoles = allowedRolesForReassignment;
    modalPayload.enableReassign = allowedRolesForReassignment !== null ?
    !!(allowedRolesForReassignment.find(role => role.toLocaleLowerCase() === this.authService.getUser().role.toLocaleLowerCase())) : true;
    modalPayload.practitionerFilterBy = modalPayload.analysis.practitionerFilterBy;
    modalPayload.disallowedPractitionerRoles = ['Provider'];
    modalPayload.modalName = 'ReassignTest';
    modalPayload.title = modalPayload.name + ', Item No. ' + modalPayload.testNumber;

    this.modalLoaderService.popupModal(ReassignTestModalComponent,
      {
        content: modalPayload,
        backdropAllowed: false
      }, ModalTypes.NONE).subscribe((data) => {
        if (data && data.payload && data.payload.action === 'close') {
          this.routerManagerService.reload();
        }
      });
  }

  public editModeIsSupported(): boolean {
    if (this.readOnlyParameters !== undefined) {
      return this.isPerformer;
    }
    return true;
  }

  public patientDetails() {
    this.patientInfoService.openPatientInfo(this.routerManagerService.parameters?.patientId).subscribe(res => {
      console.log(res);
    });
  }
}
